import React, { useState, useEffect, useRef, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import CfLoader from "../CfLoader/CfLoader";
import { ChevronLeft, ChevronRight } from "lucide-react";
import VerificationApprovalModal from "../VerificationApprovalModal/VerificationApprovalModal";
import Toast from "../Toast/Toast";
import { detectFileType } from "../../utils/DetectFileType";
import renderPdf from "../RenderPdf/RenderPdf";
import { useTranslation } from "react-i18next";

const LegacyMatchesModal = ({
  isOpen,
  certificateData,
  openCloseModal,
  handleConfirmAction,
  transactionId,
  matchingVCs,
  testId = "legacy-matches-modal",
}) => {
  const pdfCanvasRef = useRef(null);
  const { t } = useTranslation();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadingImages, setLoadingImages] = useState(
    certificateData?.length ? Array(certificateData.length).fill(true) : [],
  );
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [issuerComments, setIssuerComments] = useState("");
  const totalCertificates = certificateData?.length || 0;

  useEffect(() => {
    if (actionType && !isActionModalOpen) {
      setIsActionModalOpen(true);
    }
  }, [actionType, isActionModalOpen]);

  const ImageLoading = (
    <div className="flex items-center justify-center w-full h-auto max-h-[36rem]">
      <CfLoader />
    </div>
  );

  const handleImageLoad = (index) => {
    setLoadingImages((prev) => {
      const newLoading = [...prev];
      newLoading[index] = false;
      return newLoading;
    });
  };

  useEffect(() => {
    if (
      certificateData?.[currentIndex] &&
      detectFileType(certificateData[currentIndex]) === "pdf" &&
      pdfCanvasRef.current
    ) {
      renderPdf(certificateData[currentIndex], pdfCanvasRef)
        .then(() => handleImageLoad(currentIndex))
        .catch((error) => {
          console.error(
            `Failed to render PDF: ${certificateData[currentIndex]}`,
            error,
          );
          handleImageLoad(currentIndex);
        });
    }
  }, [certificateData, currentIndex]);

  const handleNext = () => {
    if (currentIndex < totalCertificates - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleModalConfirmAction = async () => {
    try {
      await handleConfirmAction(actionType, issuerComments);
      setIsActionModalOpen(false);
      openCloseModal(false);
    } catch (error) {
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:error_processing_action"),
      });
    }
  };

  const toggleActionModal = (isOpen) => {
    setIsActionModalOpen(isOpen);
    if (!isOpen) {
      setActionType("");
      setIssuerComments("");
    }
  };

  if (!certificateData || certificateData.length === 0) {
    return (
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => openCloseModal(false)}
        >
          {/* ... (empty state remains the same) */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              id={`${testId}-backdrop`}
              className="fixed inset-0 top-20 bg-black/30 backdrop-blur-sm"
            />
          </Transition.Child>
          <div className="fixed inset-0 top-20 overflow-y-auto px-4 md:px-0">
            <div className="flex items-center min-h-full justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  id={testId}
                  className="w-full max-w-4xl transform rounded-2xl bg-white text-left shadow-xl transition-all"
                >
                  <button
                    type="button"
                    id={`${testId}-close`}
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-500"
                    onClick={() => openCloseModal(false)}
                  >
                    ✕
                  </button>
                  <div className="p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">
                      {t("verificationApprovals:matching_certificates_count")}
                    </h2>
                    <p className="text-gray-500">
                      {t(
                        "verificationApprovals:no_certificates_available_display",
                      )}
                    </p>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => openCloseModal(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            id={`${testId}-backdrop`}
            className="fixed inset-0 top-20 bg-black/30 backdrop-blur-sm"
          />
        </Transition.Child>

        <div className="fixed inset-0 top-20 overflow-y-auto px-4 md:px-0 max-h-full">
          <div className="flex items-center min-h-full justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id={testId}
                className="w-full max-w-4xl max-h-[85vh] transform rounded-2xl bg-white text-left shadow-xl transition-all overflow-y-auto relative"
              >
                <button
                  type="button"
                  id={`${testId}-close`}
                  className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={() => openCloseModal(false)}
                >
                  &#10005;
                </button>

                {/* Navigation Buttons */}
                <button
                  type="button"
                  id={`${testId}-prev`}
                  onClick={handlePrev}
                  disabled={currentIndex === 0}
                  className={`absolute left-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full shadow-lg transition-all duration-200 ${
                    currentIndex === 0
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:shadow-xl"
                  }`}
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  id={`${testId}-next`}
                  onClick={handleNext}
                  disabled={currentIndex === totalCertificates - 1}
                  className={`absolute right-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full shadow-lg transition-all duration-200 ${
                    currentIndex === totalCertificates - 1
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:shadow-xl"
                  }`}
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    {t("verificationApprovals:matching_certificates")} (
                    {currentIndex + 1})
                  </h2>

                  {/* Certificate Display */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    {loadingImages[currentIndex] && ImageLoading}
                    {certificateData[currentIndex] &&
                    typeof certificateData[currentIndex] === "string" ? (
                      detectFileType(certificateData[currentIndex]) ===
                      "pdf" ? (
                        <canvas
                          ref={pdfCanvasRef}
                          className="w-full h-auto max-h-[36rem] object-contain"
                        />
                      ) : (
                        <img
                          src={certificateData[currentIndex]}
                          alt={`${t(
                            "verificationApprovals:matching_certificate",
                          )} ${currentIndex + 1}`}
                          className={`w-full h-auto max-h-[36rem] object-contain ${
                            loadingImages[currentIndex] ? "hidden" : "block"
                          }`}
                          onLoad={() => handleImageLoad(currentIndex)}
                          onError={() => {
                            handleImageLoad(currentIndex);
                          }}
                        />
                      )
                    ) : (
                      <p className="text-gray-500">
                        {t(
                          "verificationApprovals:certificate_url_not_available",
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>

        {/* Action Confirmation Modal */}
        <VerificationApprovalModal
          isModalOpen={isActionModalOpen}
          toggleModal={toggleActionModal}
          actionType={actionType}
          handleConfirmAction={handleModalConfirmAction}
          transaction_id={transactionId}
          issuerComments={issuerComments}
          setIssuerComments={setIssuerComments}
          isLegacy={true}
          matchingVCs={matchingVCs}
        />
      </Dialog>
    </Transition>
  );
};

export default LegacyMatchesModal;
